import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

/**
 * TeachItBackView - "Teach the AI" Experience
 *
 * The Feynman Technique meets prompt engineering practice.
 * Student explains a concept TO Mira, who plays a "confused student."
 *
 * Design Principles:
 * - Apple: Clean, focused, one primary action
 * - Discord: Social hooks (share explanations, leaderboards)
 * - Google: Clear feedback on what's working
 * - Cash App: Remove uncertainty - always show state
 *
 * Learning Science:
 * - Explaining concepts = deepest understanding (Feynman)
 * - Iterating based on AI questions = prompt engineering
 * - Scoring = gamification that drives quality
 */

// Mira's emotional states as a "confused student"
const MIRA_STUDENT_STATES = {
  curious: {
    emoji: '🤔',
    label: 'Curious',
    color: '#6DB4CD',
    message: "I'm ready to learn!"
  },
  listening: {
    emoji: '👂',
    label: 'Listening',
    color: '#4d99a5',
    message: 'Go on...'
  },
  confused: {
    emoji: '😕',
    label: 'Confused',
    color: '#f59e0b',
    message: "Wait, I don't get it..."
  },
  thinking: {
    emoji: '🧐',
    label: 'Thinking',
    color: '#8b5cf6',
    message: 'Hmm, let me think...'
  },
  understanding: {
    emoji: '😮',
    label: 'Getting it',
    color: '#10b981',
    message: 'Ohhh...'
  },
  enlightened: {
    emoji: '🤩',
    label: 'Got it!',
    color: '#2DD4BF',
    message: 'That makes so much sense!'
  }
};

// Difficulty labels optimized for Gen Z engagement
const DIFFICULTY_CONFIG = {
  'Quick Win': { color: '#10b981', bg: '#dcfce7', label: '⚡ Quick Win' },
  'Solid': { color: '#3b82f6', bg: '#dbeafe', label: '💪 Solid' },
  'Big Brain': { color: '#8b5cf6', bg: '#ede9fe', label: '🧠 Big Brain' },
};

// Topics for 6-12 grade - relatable names with academic subtitles
const SAMPLE_TOPICS = [
  // GEOMETRY - Visual, tangible concepts
  { id: 'pythagorean', name: 'a² + b² = c²', subtitle: 'Pythagorean Theorem', subject: 'Geometry', difficulty: 'Solid' },
  { id: 'triangle-angles', name: 'Why Triangles = 180°', subtitle: 'Triangle Angle Sum', subject: 'Geometry', difficulty: 'Quick Win' },
  { id: 'similar-triangles', name: 'Same Shape, Different Size', subtitle: 'Similar Triangles', subject: 'Geometry', difficulty: 'Solid' },
  { id: 'circle-parts', name: 'Pi and All That', subtitle: 'Circle Properties', subject: 'Geometry', difficulty: 'Quick Win' },
  { id: 'area-perimeter', name: 'Inside vs Around', subtitle: 'Area & Perimeter', subject: 'Geometry', difficulty: 'Quick Win' },
  { id: 'volume-3d', name: '3D Shape Math', subtitle: 'Volume Formulas', subject: 'Geometry', difficulty: 'Big Brain' },
  { id: 'parallel-lines', name: 'Lines That Never Meet', subtitle: 'Parallel Lines & Angles', subject: 'Geometry', difficulty: 'Solid' },

  // ALGEBRA - Answer "when will I use this?"
  { id: 'fractions', name: 'Adding Fractions', subtitle: 'Common Denominators', subject: 'Math', difficulty: 'Quick Win' },
  { id: 'slope', name: 'Rise Over Run', subtitle: 'Slope of a Line', subject: 'Algebra', difficulty: 'Solid' },
  { id: 'linear-equations', name: 'Solve for X', subtitle: 'Linear Equations', subject: 'Algebra', difficulty: 'Solid' },
  { id: 'exponents', name: 'Powers & Squares', subtitle: 'Exponent Rules', subject: 'Algebra', difficulty: 'Solid' },
  { id: 'quadratics', name: 'The Parabola Thing', subtitle: 'Quadratic Equations', subject: 'Algebra', difficulty: 'Big Brain' },
  { id: 'pemdas', name: 'Order of Operations', subtitle: 'PEMDAS', subject: 'Math', difficulty: 'Quick Win' },

  // SCIENCE - Make it feel relevant
  { id: 'photosynthesis', name: 'How Plants Eat', subtitle: 'Photosynthesis', subject: 'Biology', difficulty: 'Solid' },
  { id: 'gravity', name: 'Why Things Fall', subtitle: 'Gravity', subject: 'Physics', difficulty: 'Solid' },
  { id: 'mitosis', name: 'How Cells Copy', subtitle: 'Cell Division', subject: 'Biology', difficulty: 'Solid' },
  { id: 'water-cycle', name: 'Rain → River → Cloud', subtitle: 'Water Cycle', subject: 'Earth Science', difficulty: 'Quick Win' },
  { id: 'newtons-laws', name: "Newton's 3 Rules", subtitle: 'Laws of Motion', subject: 'Physics', difficulty: 'Big Brain' },
  { id: 'atoms', name: 'Tiny Building Blocks', subtitle: 'Atomic Structure', subject: 'Chemistry', difficulty: 'Solid' },
  { id: 'ecosystems', name: 'Who Eats Who', subtitle: 'Food Chains', subject: 'Biology', difficulty: 'Quick Win' },

  // HISTORY - Stories > dates
  { id: 'civil-war', name: 'Why America Split', subtitle: 'Civil War Causes', subject: 'History', difficulty: 'Big Brain' },
  { id: 'three-branches', name: 'How Laws Get Made', subtitle: '3 Branches of Gov', subject: 'Civics', difficulty: 'Solid' },
  { id: 'american-revolution', name: 'Breaking Up with Britain', subtitle: 'American Revolution', subject: 'History', difficulty: 'Solid' },
  { id: 'civil-rights', name: 'Fighting for Equality', subtitle: 'Civil Rights Movement', subject: 'History', difficulty: 'Solid' },

  // ENGLISH - Creative thinking
  { id: 'metaphor', name: 'Like vs Is', subtitle: 'Metaphors & Similes', subject: 'English', difficulty: 'Quick Win' },
  { id: 'theme', name: "The Story's Real Point", subtitle: 'Theme vs Main Idea', subject: 'English', difficulty: 'Solid' },
  { id: 'thesis', name: 'Your Main Argument', subtitle: 'Thesis Statements', subject: 'English', difficulty: 'Big Brain' },
];

/**
 * MiraStudentAvatar - Animated avatar showing Mira as a student
 */
const MiraStudentAvatar = ({ state = 'curious', size = 120 }) => {
  const stateConfig = MIRA_STUDENT_STATES[state] || MIRA_STUDENT_STATES.curious;

  return (
    <motion.div
      className="mira-student-avatar"
      animate={{
        scale: state === 'enlightened' ? [1, 1.1, 1] : 1,
      }}
      transition={{ duration: 0.5 }}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${stateConfig.color}40, ${stateConfig.color}20)`,
        border: `3px solid ${stateConfig.color}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.45,
        boxShadow: `0 8px 32px ${stateConfig.color}30`,
        position: 'relative',
      }}
    >
      <motion.span
        key={state}
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      >
        {stateConfig.emoji}
      </motion.span>

      {/* Pulse ring for listening state */}
      {state === 'listening' && (
        <motion.div
          style={{
            position: 'absolute',
            inset: -8,
            borderRadius: '50%',
            border: `2px solid ${stateConfig.color}`,
          }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.8, 0, 0.8] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />
      )}
    </motion.div>
  );
};

// Subject categories for filtering
const SUBJECT_FILTERS = [
  { id: 'all', label: 'All', icon: '📚' },
  { id: 'math', label: 'Math', icon: '📐', subjects: ['Math', 'Algebra', 'Geometry'] },
  { id: 'science', label: 'Science', icon: '🔬', subjects: ['Biology', 'Physics', 'Chemistry', 'Earth Science'] },
  { id: 'history', label: 'History', icon: '📜', subjects: ['History', 'Civics'] },
  { id: 'english', label: 'English', icon: '✏️', subjects: ['English'] },
];

/**
 * TopicSelector - Grid of topics to teach with subject filtering
 */
const TopicSelector = ({ topics, onSelect, selectedTopic }) => {
  const [activeFilter, setActiveFilter] = useState('all');

  const filteredTopics = activeFilter === 'all'
    ? topics
    : topics.filter(t => {
        const filter = SUBJECT_FILTERS.find(f => f.id === activeFilter);
        return filter?.subjects?.includes(t.subject);
      });

  return (
    <div className="topic-selector">
      {/* Subject Filter Pills */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
        overflowX: 'auto',
        paddingBottom: '4px',
      }}>
        {SUBJECT_FILTERS.map((filter) => (
          <motion.button
            key={filter.id}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveFilter(filter.id)}
            style={{
              padding: '8px 14px',
              background: activeFilter === filter.id
                ? 'linear-gradient(135deg, #297A92, #3d9bb0)'
                : '#f1f5f9',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
              color: activeFilter === filter.id ? 'white' : '#64748b',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s ease',
            }}
          >
            {filter.icon} {filter.label}
          </motion.button>
        ))}
      </div>

      <h3 style={{
        fontSize: '14px',
        fontWeight: 600,
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '12px',
      }}>
        Pick a topic to teach
      </h3>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: '10px',
        maxHeight: '400px',
        overflowY: 'auto',
        paddingRight: '4px',
      }}>
        {filteredTopics.map((topic) => {
          const diffConfig = DIFFICULTY_CONFIG[topic.difficulty] || DIFFICULTY_CONFIG['Solid'];
          const isSelected = selectedTopic?.id === topic.id;

          return (
            <motion.button
              key={topic.id}
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => onSelect(topic)}
              style={{
                padding: '14px 12px',
                background: isSelected
                  ? 'linear-gradient(135deg, #297A92, #3d9bb0)'
                  : 'white',
                border: isSelected
                  ? '1px solid #297A92'
                  : '1px solid #e2e8f0',
                borderRadius: '12px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s ease',
                boxShadow: isSelected
                  ? '0 4px 12px rgba(41, 122, 146, 0.2)'
                  : '0 2px 4px rgba(0, 0, 0, 0.02)',
              }}
            >
              {/* Catchy Name */}
              <div style={{
                fontSize: '14px',
                fontWeight: 600,
                color: isSelected ? 'white' : '#1e293b',
                marginBottom: '2px',
                lineHeight: 1.3,
              }}>
                {topic.name}
              </div>
              {/* Academic Subtitle */}
              {topic.subtitle && (
                <div style={{
                  fontSize: '11px',
                  color: isSelected ? 'rgba(255,255,255,0.7)' : '#94a3b8',
                  marginBottom: '8px',
                }}>
                  {topic.subtitle}
                </div>
              )}
              {/* Subject + Difficulty */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '6px',
              }}>
                <span style={{
                  fontSize: '11px',
                  color: isSelected ? 'rgba(255,255,255,0.8)' : '#64748b',
                }}>
                  {topic.subject}
                </span>
                <span style={{
                  fontSize: '10px',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  background: isSelected ? 'rgba(255,255,255,0.2)' : diffConfig.bg,
                  color: isSelected ? 'white' : diffConfig.color,
                  fontWeight: 500,
                }}>
                  {diffConfig.label}
                </span>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * ExplanationScore - Shows how well the student explained
 */
const ExplanationScore = ({ score, feedback, tokensEarned, onTryAgain, onShare }) => {
  const getScoreColor = (s) => {
    if (s >= 80) return '#10b981';
    if (s >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const getScoreLabel = (s) => {
    if (s >= 90) return 'Excellent!';
    if (s >= 80) return 'Great job!';
    if (s >= 70) return 'Good effort!';
    if (s >= 60) return 'Getting there!';
    return 'Keep practicing!';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="explanation-score"
      style={{
        background: 'white',
        borderRadius: '20px',
        padding: '24px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        border: '1px solid #e2e8f0',
        maxWidth: '400px',
        width: '100%',
      }}
    >
      {/* Score Circle */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '20px',
      }}>
        <div style={{
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: `conic-gradient(${getScoreColor(score)} ${score * 3.6}deg, #e2e8f0 0deg)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'white',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <span style={{
              fontSize: '28px',
              fontWeight: 700,
              color: getScoreColor(score),
            }}>
              {score}
            </span>
            <span style={{
              fontSize: '11px',
              color: '#94a3b8',
            }}>
              / 100
            </span>
          </div>
        </div>
        <div style={{
          marginTop: '12px',
          fontSize: '18px',
          fontWeight: 600,
          color: getScoreColor(score),
        }}>
          {getScoreLabel(score)}
        </div>
      </div>

      {/* Feedback Items */}
      <div style={{ marginBottom: '20px' }}>
        {feedback.map((item, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 0',
              borderBottom: i < feedback.length - 1 ? '1px solid #f1f5f9' : 'none',
            }}
          >
            <span style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: item.positive ? '#dcfce7' : '#fef3c7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
            }}>
              {item.positive ? '✓' : '!'}
            </span>
            <span style={{
              fontSize: '13px',
              color: '#475569',
            }}>
              {item.text}
            </span>
          </div>
        ))}
      </div>

      {/* Tokens Earned */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '12px',
        background: 'linear-gradient(135deg, rgba(41, 122, 146, 0.1), rgba(109, 180, 205, 0.1))',
        borderRadius: '12px',
        marginBottom: '16px',
      }}>
        <span style={{ fontSize: '20px' }}>💎</span>
        <span style={{
          fontSize: '16px',
          fontWeight: 600,
          color: '#297A92',
        }}>
          +{tokensEarned} tokens earned
        </span>
      </div>

      {/* Actions */}
      <div style={{
        display: 'flex',
        gap: '10px',
      }}>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onTryAgain}
          style={{
            flex: 1,
            padding: '12px',
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: 500,
            color: '#475569',
            cursor: 'pointer',
          }}
        >
          Try Another
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onShare}
          style={{
            flex: 1,
            padding: '12px',
            background: 'linear-gradient(135deg, #297A92, #3d9bb0)',
            border: 'none',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: 500,
            color: 'white',
            cursor: 'pointer',
          }}
        >
          Share with Friends
        </motion.button>
      </div>
    </motion.div>
  );
};

/**
 * TeachItBackView - Main component
 */
const TeachItBackView = ({
  user,
  onClose,
  onStartVoice,        // Callback to start voice session with special prompt
  onScoreSubmit,       // Callback when explanation is scored
  isVoiceActive = false,
  agentState = 'idle',
  className = '',
}) => {
  const [phase, setPhase] = useState('select'); // 'select' | 'intro' | 'teaching' | 'scoring' | 'results'
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [miraState, setMiraState] = useState('curious');
  const [miraMessage, setMiraMessage] = useState("Pick a topic and teach me!");
  const [explanationScore, setExplanationScore] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState(''); // Track user's explanation
  const [miraQuestions, setMiraQuestions] = useState([]); // Track Mira's clarifying questions

  // Simulate Mira's reactions during teaching (will be replaced by real AI)
  useEffect(() => {
    if (phase === 'teaching' && isVoiceActive) {
      // Map agent state to Mira student state
      if (agentState === 'listening') {
        setMiraState('listening');
        setMiraMessage('Go on, I\'m listening...');
      } else if (agentState === 'thinking') {
        setMiraState('thinking');
        setMiraMessage('Hmm, let me think about that...');
      } else if (agentState === 'speaking') {
        // Mira asking a clarifying question
        setMiraState('confused');
        setMiraMessage('Wait, can you explain that part again?');
      }
    }
  }, [phase, isVoiceActive, agentState]);

  // Handle topic selection
  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic);
    setPhase('intro');
    setMiraState('curious');
    // Use the catchy name for a more engaging message
    const displayName = topic.subtitle || topic.name;
    setMiraMessage(`Ooh, ${displayName}! I keep getting confused about this. Can you help me understand?`);
  };

  // Start the teaching session
  const handleStartTeaching = () => {
    setPhase('teaching');
    setMiraState('listening');
    setMiraMessage("I'm ready! Explain it to me like I'm your friend who missed class.");

    // Call parent to start voice with "confused student" prompt
    // Use subtitle (academic name) for the actual concept, catchy name for personality
    const academicTopic = selectedTopic.subtitle || selectedTopic.name;
    if (onStartVoice) {
      onStartVoice({
        mode: 'teach_it_back',
        topic: selectedTopic,
        systemPrompt: `You are Mira, a friendly but confused student who needs ${academicTopic} explained to them.
        Act like a peer who missed class and genuinely doesn't understand.
        Ask clarifying questions when the explanation is unclear.
        Use casual, teen-friendly language - you're a Gen Z student.
        When they explain well, show genuine understanding ("Ohhh that makes sense!" or "Wait, so that's why...").
        When confused, ask things like "But why does that work?" or "Can you give me an example?"
        Don't be a teacher - be a student trying to learn.`
      });
    }
  };

  // Score the explanation using the backend API
  const handleFinishExplanation = async () => {
    setPhase('scoring');
    setMiraState('thinking');
    setMiraMessage('Let me think about how well I understood that...');

    try {
      // Call the scoring API - use academic name (subtitle) for better LLM evaluation
      const academicTopic = selectedTopic?.subtitle || selectedTopic?.name || selectedTopic;
      const response = await axios.post('/api/voice/score-explanation', {
        topic: academicTopic,
        transcript: transcript || 'User explained the concept verbally.', // Fallback if no transcript captured
        mira_questions: miraQuestions
      });

      if (response.data.success) {
        const scoreData = {
          score: response.data.score,
          feedback: response.data.feedback,
          tokensEarned: response.data.tokens_earned,
          breakdown: response.data.breakdown,
          summary: response.data.summary
        };

        setExplanationScore(scoreData);
        setPhase('results');
        setMiraState('enlightened');
        setMiraMessage(response.data.summary || 'I get it now! Thanks for teaching me!');

        if (onScoreSubmit) {
          onScoreSubmit(scoreData);
        }
      } else {
        // Fallback to mock score on API error
        console.warn('Scoring API returned error, using fallback');
        applyFallbackScore();
      }
    } catch (error) {
      console.error('Error calling scoring API:', error);
      // Fallback to mock score on network error
      applyFallbackScore();
    }
  };

  // Fallback scoring when API is unavailable
  const applyFallbackScore = () => {
    const mockScore = {
      score: Math.floor(Math.random() * 30) + 70, // 70-100 for MVP
      feedback: [
        { text: 'Good effort explaining the concept', positive: true },
        { text: 'Keep practicing to improve!', positive: true },
        { text: 'Try adding more examples next time', positive: false },
      ],
      tokensEarned: Math.floor(Math.random() * 20) + 30,
    };
    setExplanationScore(mockScore);
    setPhase('results');
    setMiraState('enlightened');
    setMiraMessage('I get it now! Thanks for teaching me!');

    if (onScoreSubmit) {
      onScoreSubmit(mockScore);
    }
  };

  // Reset for another topic
  const handleTryAgain = () => {
    setPhase('select');
    setSelectedTopic(null);
    setExplanationScore(null);
    setMiraState('curious');
    setMiraMessage("Pick a topic and teach me!");
  };

  return (
    <div className={`teach-it-back-view ${className}`} style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px',
      minHeight: '100%',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        maxWidth: '500px',
        marginBottom: '24px',
      }}>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onClose}
          style={{
            padding: '8px 12px',
            background: 'transparent',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            color: '#64748b',
            fontSize: '14px',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6"/>
          </svg>
          Back
        </motion.button>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span style={{
            fontSize: '13px',
            fontWeight: 600,
            color: '#297A92',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            Teach It Back
          </span>
          <span style={{
            padding: '4px 8px',
            background: 'linear-gradient(135deg, #297A92, #3d9bb0)',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: 600,
            color: 'white',
          }}>
            +50 tokens
          </span>
        </div>
      </div>

      {/* Mira Avatar */}
      <MiraStudentAvatar state={miraState} size={phase === 'teaching' ? 140 : 100} />

      {/* Mira's Message Bubble */}
      <motion.div
        key={miraMessage}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          marginTop: '16px',
          padding: '12px 20px',
          background: 'white',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
          maxWidth: '320px',
          textAlign: 'center',
        }}
      >
        <p style={{
          fontSize: '15px',
          color: '#334155',
          margin: 0,
          lineHeight: 1.5,
        }}>
          {miraMessage}
        </p>
      </motion.div>

      {/* Phase-specific content */}
      <div style={{
        marginTop: '24px',
        width: '100%',
        maxWidth: '500px',
      }}>
        <AnimatePresence mode="wait">
          {/* Topic Selection Phase */}
          {phase === 'select' && (
            <motion.div
              key="select"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <TopicSelector
                topics={SAMPLE_TOPICS}
                selectedTopic={selectedTopic}
                onSelect={handleTopicSelect}
              />
            </motion.div>
          )}

          {/* Intro Phase - Topic selected, ready to teach */}
          {phase === 'intro' && selectedTopic && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <div style={{
                padding: '16px 24px',
                background: 'linear-gradient(135deg, rgba(41, 122, 146, 0.1), rgba(109, 180, 205, 0.05))',
                borderRadius: '12px',
                border: '1px solid rgba(41, 122, 146, 0.2)',
                textAlign: 'center',
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '4px',
                }}>
                  Teaching
                </div>
                <div style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#1e293b',
                  marginBottom: selectedTopic.subtitle ? '4px' : 0,
                }}>
                  {selectedTopic.name}
                </div>
                {selectedTopic.subtitle && (
                  <div style={{
                    fontSize: '14px',
                    color: '#297A92',
                    fontWeight: 500,
                  }}>
                    {selectedTopic.subtitle}
                  </div>
                )}
              </div>

              <div style={{
                fontSize: '13px',
                color: '#64748b',
                textAlign: 'center',
                maxWidth: '280px',
              }}>
                Explain this concept as if I'm your friend who missed class.
                I'll ask questions if I don't understand!
              </div>

              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleStartTeaching}
                style={{
                  padding: '14px 32px',
                  background: 'linear-gradient(135deg, #297A92, #3d9bb0)',
                  border: 'none',
                  borderRadius: '14px',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  boxShadow: '0 8px 24px rgba(41, 122, 146, 0.3)',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" x2="12" y1="19" y2="22"/>
                </svg>
                Start Explaining
              </motion.button>

              <button
                onClick={() => setPhase('select')}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '13px',
                  color: '#64748b',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Choose different topic
              </button>
            </motion.div>
          )}

          {/* Teaching Phase - Voice active */}
          {phase === 'teaching' && (
            <motion.div
              key="teaching"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '20px',
              }}
            >
              {/* Recording indicator */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: 'rgba(239, 68, 68, 0.1)',
                borderRadius: '100px',
              }}>
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#ef4444',
                  }}
                />
                <span style={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#ef4444',
                }}>
                  Recording your explanation
                </span>
              </div>

              {/* Topic reminder */}
              <div style={{
                fontSize: '14px',
                color: '#64748b',
                textAlign: 'center',
              }}>
                Teaching: <strong style={{ color: '#1e293b' }}>{selectedTopic?.name}</strong>
                {selectedTopic?.subtitle && (
                  <span style={{ color: '#297A92' }}> ({selectedTopic.subtitle})</span>
                )}
              </div>

              {/* Finish button */}
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleFinishExplanation}
                style={{
                  padding: '12px 24px',
                  background: '#10b981',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                I'm Done Explaining
              </motion.button>
            </motion.div>
          )}

          {/* Scoring Phase */}
          {phase === 'scoring' && (
            <motion.div
              key="scoring"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                style={{
                  width: '40px',
                  height: '40px',
                  border: '3px solid #e2e8f0',
                  borderTopColor: '#297A92',
                  borderRadius: '50%',
                }}
              />
              <p style={{
                fontSize: '14px',
                color: '#64748b',
              }}>
                Analyzing your explanation...
              </p>
            </motion.div>
          )}

          {/* Results Phase */}
          {phase === 'results' && explanationScore && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <ExplanationScore
                score={explanationScore.score}
                feedback={explanationScore.feedback}
                tokensEarned={explanationScore.tokensEarned}
                onTryAgain={handleTryAgain}
                onShare={() => {
                  // TODO: Implement share functionality
                  alert('Share feature coming soon!');
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Inline styles for animations */}
      <style>{`
        .teach-it-back-view {
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          min-height: 100vh;
        }

        @media (max-width: 480px) {
          .teach-it-back-view {
            padding: 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default TeachItBackView;
